import type { HTMLAttributes } from "react";
import type {
  DeepKeys,
  DeepValue,
  ValidationError,
} from "@tanstack/react-form";
import type { z } from "zod";

import { cn } from "@/lib/utils";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod } from "@/utils/form";

export type FieldErrorsProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
> = Omit<
  FieldBaseProps<TFormData, TName, TSchema, TData>,
  "label" | "labelProps"
> &
  HTMLAttributes<HTMLElement>;

export const Errors = ({
  children,
  className,
  ...props
}: { children?: Array<ValidationError> } & Omit<
  HTMLAttributes<HTMLElement>,
  "children"
>) => {
  return children && children.length ? (
    <em className={cn("text-destructive text-sm", className)} {...props}>
      {children.join(", ")}
    </em>
  ) : null;
};

const FieldErrors = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  validators,
  gridRow,
  ...props
}: FieldErrorsProps<TFormData, TName, TSchema, TData>) => {
  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(validators as any), // unsolved typing issue
      }}
      children={(field) => (
        <Errors
          {...props}
          className={
            gridRow ? cn("col-span-full", props.className) : props.className
          }
        >
          {field.state.meta.errors}
        </Errors>
      )}
    />
  );
};

export default FieldErrors;
